// Account-specific personal data kept in this browser. Cleared on sign-out, session expiry and account switch.
// Server-side records are never touched here. Device preferences (language, cookie consent, analytics ids) are kept.
export const ACCOUNT_DATA_PREFIXES = ['shs.academy.', 'shs_topic_chat_'];
export const ACCOUNT_DATA_KEYS = new Set([
  // Health and sleep
  'shs_sleep_journal', 'shs_sleep_journal_summary', 'sleep_journal_entries', 'shs_sleep_assessment', 'shs_sleep_assessment_progress',
  'sleep_assessment_results', 'shs_wearable_data', 'wearable_data', 'shs_sleep_profile', 'shs_sleep_activity', 'shs_sleep_streak',
  'shs_unified_profile', 'shs_dynamic_score_history', 'cpap_adherence_state', 'shs_mask_fit_history', 'cpap_user_story',
  'shs_cpap_session', 'shs_session_data', 'shs_report_purchase',
  // AI and coach conversations
  'shs_coach_chat',
  // Learning history and progress
  'shs_exam_history', 'shs_exam_streak', 'shs_exam_achievements', 'shs_study_plan', 'shs_academy_progress', 'shs_achievements',
  'shs_milestone_celebrations', 'shs_practice_streak', 'shs_progress_synced', 'shs_academy_gamification', 'shs_academy_milestones',
  'shs_academy_reminders', 'shs_simulator_history', 'shs_certificates', 'shs_scoring_progress', 'shs_track_progress', 'shs_badges',
  'shs_sleep_points', 'shs_user_stats', 'shs_streak_data', 'shs_daily_checkin', 'shs_last_activity', 'module_progress',
  'recently_viewed_topics', 'shs_viewed_topics', 'shs_bookmarked_topics', 'shs_last_viewed_topic', 'shs_user_interactions',
  'shs_last_recommendation', 'shs_last_section', 'nba_user_actions', 'nba_dismissed', 'nba_last_shown',
  // Account and admin
  'shs_education_auth', 'shs_user_type', 'shs_email_preferences', 'isAdminLoggedIn', 'education_token',
  'shs_executive_alerts_history', 'shs_executive_alerts_queue', 'shs_executive_alerts_settings', 'shs_daily_alert_stats',
  'shs_last_revenue_milestone',
]);
export const OWNER_KEY = 'shs.academy.owner.v1';
const GUEST_STASH_KEY = 'shs.device.guest_stash.v1';
const PROMPT_DISMISSED_KEY = 'shs.device.guest_prompt_dismissed.v1';
export const GUEST_DATA_EVENT = 'shs:guest-data-changed';

export const isAccountKey = (k) => k !== OWNER_KEY && (ACCOUNT_DATA_KEYS.has(k) || ACCOUNT_DATA_PREFIXES.some((p) => k.startsWith(p)));
const storages = () => ({ local: localStorage, session: sessionStorage });
const accountKeys = (s) => Object.keys(s).filter(isAccountKey);
const notify = () => {
  window.dispatchEvent(new Event('shs:academy-reset'));
  window.dispatchEvent(new Event(GUEST_DATA_EVENT));
};

export const hasAccountData = () => Object.values(storages()).some((s) => accountKeys(s).length > 0);

export const purgeAccountData = () => {
  Object.values(storages()).forEach((s) => accountKeys(s).forEach((k) => s.removeItem(k)));
  localStorage.removeItem(PREVIEW_STASH_KEY);
  notify();
};

// Founder Preview: the Founder's own learning/health data is set aside for the preview and restored afterwards.
const PREVIEW_STASH_KEY = 'shs.device.preview_stash.v1';
const SESSION_KEYS = new Set(['shs_education_auth', 'shs_user_type', 'isAdminLoggedIn', 'education_token', 'shs_email_preferences']);
const personalKeys = (s) => accountKeys(s).filter((k) => !SESSION_KEYS.has(k));

export const stashForPreview = (expiresAt) => {
  if (localStorage.getItem(PREVIEW_STASH_KEY)) return;
  const st = { expires_at: expiresAt, local: {}, session: {} };
  Object.entries(storages()).forEach(([name, s]) => personalKeys(s).forEach((k) => { st[name][k] = s.getItem(k); s.removeItem(k); }));
  localStorage.setItem(PREVIEW_STASH_KEY, JSON.stringify(st));
  notify();
};

export const restoreAfterPreview = () => {
  let st = null;
  try { st = JSON.parse(localStorage.getItem(PREVIEW_STASH_KEY)); } catch { st = null; }
  if (!st) return;
  Object.values(storages()).forEach((s) => personalKeys(s).forEach((k) => s.removeItem(k)));
  ['local', 'session'].forEach((name) => Object.entries(st[name] || {}).forEach(([k, v]) => storages()[name].setItem(k, v)));
  localStorage.removeItem(PREVIEW_STASH_KEY);
  notify();
};

// A preview that ended without a clean exit (closed tab): restore once its deadline has passed.
export const restoreExpiredPreviewStash = () => {
  try {
    const st = JSON.parse(localStorage.getItem(PREVIEW_STASH_KEY));
    if (st && new Date(st.expires_at) <= new Date()) restoreAfterPreview();
  } catch { /* noop */ }
};

const readStash = () => {
  try { return JSON.parse(localStorage.getItem(GUEST_STASH_KEY)) || { local: {}, session: {} }; } catch { return { local: {}, session: {} }; }
};
const writeStash = (st) => {
  if (Object.keys(st.local).length || Object.keys(st.session).length) localStorage.setItem(GUEST_STASH_KEY, JSON.stringify(st));
  else localStorage.removeItem(GUEST_STASH_KEY);
};

export const hasGuestStash = () => { const st = readStash(); return Object.keys(st.local).length + Object.keys(st.session).length > 0; };

// Guest data set aside at sign-in; it never joins the account without the learner's explicit confirmation.
export const stashGuestData = () => {
  const st = readStash();
  Object.entries(storages()).forEach(([name, s]) => accountKeys(s).forEach((k) => {
    if (!(k in st[name])) st[name][k] = s.getItem(k);
    s.removeItem(k);
  }));
  writeStash(st);
  notify();
};

// Confirmed by the learner: copy guest data in, but never overwrite data the account already has.
export const adoptGuestData = () => {
  const st = readStash();
  const kept = { local: {}, session: {} };
  let adopted = 0;
  Object.entries(storages()).forEach(([name, s]) => Object.entries(st[name]).forEach(([k, v]) => {
    if (s.getItem(k) === null) { s.setItem(k, v); adopted += 1; } else kept[name][k] = v;
  }));
  writeStash(kept);
  notify();
  return { adopted, kept: Object.keys(kept.local).length + Object.keys(kept.session).length };
};

export const restoreGuestData = () => {
  const st = readStash();
  Object.entries(storages()).forEach(([name, s]) => Object.entries(st[name]).forEach(([k, v]) => s.setItem(k, v)));
  localStorage.removeItem(GUEST_STASH_KEY);
  notify();
};

export const guestPromptDismissedFor = (uid) => localStorage.getItem(PROMPT_DISMISSED_KEY) === uid;
export const dismissGuestPrompt = (uid) => { localStorage.setItem(PROMPT_DISMISSED_KEY, uid); notify(); };
export const clearGuestPromptDismissal = () => localStorage.removeItem(PROMPT_DISMISSED_KEY);
